import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { RootState } from "@/app/rootReducer";
import { syncCompanyFromSalesforceAction } from "../../slices/companiesSlice";
import { useNotification } from "@/context/NotificationContext";
import {
  syncCompanyFromSalesforceSchema,
  type SyncCompanyFromSalesforceFormData,
} from "../../schemas/companySchemas";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon, Loader2 } from "lucide-react";

const CreateCompany: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.companies);
  const { showNotification } = useNotification();

  const form = useForm<SyncCompanyFromSalesforceFormData>({
    resolver: zodResolver(syncCompanyFromSalesforceSchema),
    defaultValues: {
      intacctId: "",
    },
    mode: "onChange",
  });

  const { handleSubmit, formState } = form;
  const { isSubmitting, isValid } = formState;

  const onSubmit = async (values: SyncCompanyFromSalesforceFormData) => {
    await dispatch(
      syncCompanyFromSalesforceAction(
        {
          intacctId: values.intacctId,
        },
        (newCompany) => {
          showNotification(
            "Company Synced",
            `"${newCompany.companyName}" has been synced from Salesforce. You can now configure additional settings.`,
            "success",
          );
          navigate(`/admin/companies/${newCompany.uuid}/edit`);
        },
      ),
    );
  };

  const isLoading = loading || isSubmitting;

  return (
    <MainPageWrapper error={null} loading={false} title="Add Company">
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              Enter the Intacct ID to sync the company and its employees from
              Salesforce. After syncing, you'll be redirected to the edit page
              where you can configure additional settings.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Sync from Salesforce</CardTitle>
              <CardDescription>
                Import company data and employees from Salesforce using the
                Intacct ID
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="intacctId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Intacct ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Intacct ID (e.g., ABC123)"
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormDescription>
                      The unique identifier for this company in
                      Intacct/Salesforce
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button
              disabled={isLoading}
              onClick={() => navigate("/admin/companies")}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={!isValid || isLoading} type="submit">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing from Salesforce...
                </>
              ) : (
                "Sync Company"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default CreateCompany;
